/**
 * User Settings Page
 * Allows users to manage their profile, view permissions, and manage sessions.
 * 
 * Tabs:
 * - Profile: Edit name, job title, department, change password
 * - My Permissions: Read-only view of role capabilities
 * - Sessions: View and manage active login sessions (AS9100D 7.5 Compliance)
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  User, Shield, Save, Eye, EyeOff, ArrowLeft, Loader2,
  CheckCircle2, XCircle, Lock, Mail, Briefcase, Building2,
  Monitor, Smartphone, Laptop, Globe, MapPin, Clock, Activity,
  LogOut, RefreshCw, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import userService from '../services/userService';
import { formatIST } from '../utils/timezone';

// =============================================================================
// PASSWORD STRENGTH INDICATOR
// =============================================================================

const PasswordStrengthIndicator = ({ password }) => {
  const checks = [
    { label: 'At least 8 characters', test: password.length >= 8 },
    { label: 'Contains uppercase letter', test: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter', test: /[a-z]/.test(password) },
    { label: 'Contains number', test: /\d/.test(password) },
    { label: 'Contains special character', test: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const passedCount = checks.filter(c => c.test).length;
  const strength = passedCount <= 2 ? 'weak' : passedCount <= 3 ? 'fair' : passedCount <= 4 ? 'good' : 'strong';
  const strengthColors = {
    weak: 'bg-red-500',
    fair: 'bg-orange-500',
    good: 'bg-yellow-500',
    strong: 'bg-green-500'
  };

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            className={`h-1 flex-1 rounded ${i <= passedCount ? strengthColors[strength] : 'bg-slate-200'}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-1 text-xs">
        {checks.map((check, idx) => (
          <div key={idx} className={`flex items-center gap-1 ${check.test ? 'text-green-600' : 'text-slate-400'}`}>
            {check.test ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {check.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// PROFILE TAB
// =============================================================================

const ProfileTab = ({ user, onUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    job_title: user?.job_title || '',
    department: user?.department || ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        job_title: user.job_title || '',
        department: user.department || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await api.put('/api/v1/users/me', formData);
      toast.success('Profile updated successfully');
      onUpdate(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.put('/api/v1/users/me/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </CardTitle>
          <CardDescription>
            Update your personal information. Changes will be reflected across the application.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email - Read Only */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-500">
              <Mail className="w-4 h-4" />
              Email Address
            </Label>
            <Input 
              value={user?.email || ''} 
              disabled 
              className="bg-slate-50"
              data-testid="profile-email"
            />
            <p className="text-xs text-slate-500">Email cannot be changed. Contact an administrator if needed.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
                data-testid="profile-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
                data-testid="profile-last-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="job_title" className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Job Title
              </Label>
              <Input
                id="job_title"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                placeholder="e.g., Senior Engineer"
                data-testid="profile-job-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="department" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Department
              </Label>
              <Input
                id="department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                placeholder="e.g., Engineering"
                data-testid="profile-department"
              />
            </div>
          </div>

          {/* Role - Read Only */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-slate-500">
              <Shield className="w-4 h-4" />
              Role
            </Label>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {user?.role?.replace('_', ' ') || 'viewer'}
              </Badge>
              <span className="text-xs text-slate-500">Role is assigned by administrators</span>
            </div>
          </div>

          <div className="pt-4 border-t">
            <Button 
              onClick={handleSaveProfile} 
              disabled={saving || !hasChanges}
              data-testid="save-profile-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update your password. You will need to enter your current password for verification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                  placeholder="Enter current password"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="Enter new password"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthIndicator password={passwordForm.new_password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
                data-testid="confirm-password"
              />
              {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
              {passwordForm.confirm_password && passwordForm.new_password === passwordForm.confirm_password && (
                <p className="text-xs text-green-500 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>

            <div className="pt-4 border-t">
              <Button 
                type="submit"
                disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                data-testid="change-password-button"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// SESSIONS TAB - AS9100D 7.5 Compliance
// =============================================================================

/**
 * Format relative time for session display
 * Note: Server timestamps are in UTC but may not have 'Z' suffix,
 * so we need to ensure proper UTC parsing
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Unknown';
  
  // Ensure UTC parsing - append 'Z' if not present and no timezone info
  let utcString = dateString;
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    utcString = dateString + 'Z';
  }
  
  const date = new Date(utcString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  // Fallback to IST formatted date
  return formatIST(dateString, { includeSeconds: false });
};

/**
 * Device icon based on user agent
 */
const DeviceIcon = ({ userAgent }) => {
  if (!userAgent) return <Monitor className="w-5 h-5 text-slate-600" />;
  
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) {
    return <Smartphone className="w-5 h-5 text-slate-600" />;
  }
  if (ua.includes('tablet') || ua.includes('ipad')) {
    return <Laptop className="w-5 h-5 text-slate-600" />;
  }
  return <Monitor className="w-5 h-5 text-slate-600" />;
};

/**
 * Parse user agent to get browser and OS info
 */
const parseUserAgent = (userAgent) => {
  if (!userAgent) return { browser: 'Unknown Browser', os: 'Unknown Device' };
  
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';
  
  // Detect browser
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Edg')) {
    browser = 'Edge';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    browser = 'Opera';
  }
  
  // Detect OS
  if (userAgent.includes('Windows')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac OS') || userAgent.includes('Macintosh')) {
    os = 'macOS';
  } else if (userAgent.includes('Linux') && !userAgent.includes('Android')) {
    os = 'Linux';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    os = 'iOS';
  }
  
  return { browser, os };
};

const SessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [terminatingId, setTerminatingId] = useState(null);
  const [terminatingAll, setTerminatingAll] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.getMySessions({ includeHistory: true, historyDays: 30 });
      setSessions(response.sessions || []);
      setHistory(response.history || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      toast.error('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleTerminate = async (sessionId) => {
    setTerminatingId(sessionId);
    try {
      await userService.terminateMySession(sessionId);
      toast.success('Session terminated successfully');
      fetchSessions();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to terminate session';
      toast.error(errorMsg);
    } finally {
      setTerminatingId(null);
    }
  };

  const handleTerminateAllOthers = async () => {
    setTerminatingAll(true);
    try {
      const result = await userService.terminateAllMyOtherSessions();
      toast.success(result.message || 'All other sessions terminated');
      fetchSessions();
    } catch (err) {
      toast.error('Failed to terminate sessions');
    } finally {
      setTerminatingAll(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
      </div>
    );
  }

  const otherSessions = sessions.filter(s => !s.is_current);
  const currentSession = sessions.find(s => s.is_current);
  const displayedHistory = showAllHistory ? history : history.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                Active Sessions
                <Badge variant="secondary" className="ml-2">
                  {sessions.length}
                </Badge>
              </CardTitle>
              <CardDescription className="mt-1">
                Manage devices where you are currently logged in
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchSessions}
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {otherSessions.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleTerminateAllOthers}
                  disabled={terminatingAll}
                  data-testid="terminate-all-sessions-btn"
                >
                  {terminatingAll ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LogOut className="w-4 h-4 mr-2" />
                  )}
                  Log Out All Other Devices
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Current Session */}
      {currentSession && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <ShieldCheck className="w-4 h-4" />
              Current Session
            </div>
          </CardHeader>
          <CardContent>
            <SessionCard session={currentSession} isCurrent={true} />
          </CardContent>
        </Card>
      )}

      {/* Other Sessions */}
      {otherSessions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Other Active Sessions
              <span className="text-slate-400 font-normal">({otherSessions.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {otherSessions.map((session) => (
              <SessionCard
                key={session.session_id}
                session={session}
                isCurrent={false}
                onTerminate={handleTerminate}
                terminatingId={terminatingId}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Session History */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Session History
                <span className="text-slate-400 font-normal">(Last 30 Days)</span>
              </CardTitle>
              {history.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  className="text-[#101957]"
                >
                  {showAllHistory ? 'Show Less' : `View All (${history.length})`}
                </Button>
              )}
            </div>
            <CardDescription>
              Previous login sessions and their termination status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {displayedHistory.map((session) => (
              <HistorySessionCard key={session.session_id} session={session} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty History State */}
      {history.length === 0 && sessions.length <= 1 && (
        <Card className="bg-slate-50">
          <CardContent className="pt-6 text-center">
            <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-600">No session history available</p>
            <p className="text-xs text-slate-500 mt-1">
              Your login history will appear here after your first logout
            </p>
          </CardContent>
        </Card>
      )}

      {/* Security Tip */}
      <Card className="bg-amber-50/50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Security Tip</p>
              <p className="text-sm text-amber-700 mt-1">
                Do not recognize a session? Terminate it immediately to protect your account. 
                If you see suspicious activity, consider changing your password.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AS9100D Compliance Note */}
      <div className="text-xs text-slate-500 flex items-center gap-2 justify-center">
        <Shield className="w-3 h-3" />
        AS9100D 7.5 Compliance: Session audit trail maintained for security verification
      </div>
    </div>
  );
};

/**
 * Individual Session Card Component
 */
const SessionCard = ({ session, isCurrent, onTerminate, terminatingId }) => {
  const { browser, os } = parseUserAgent(session.user_agent);
  const isTerminating = terminatingId === session.session_id;

  return (
    <div className={`flex items-start justify-between p-4 rounded-lg border ${
      isCurrent ? 'bg-white border-green-200' : 'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${isCurrent ? 'bg-green-100' : 'bg-white border'}`}>
          <DeviceIcon userAgent={session.user_agent} />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">
              {browser} on {os}
            </span>
            {isCurrent && (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                Current
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            {session.ip_address && (
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {session.ip_address}
              </span>
            )}
            {(session.geo_city || session.geo_country) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {[session.geo_city, session.geo_country].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Started: {formatRelativeTime(session.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Last active: {formatRelativeTime(session.last_activity_at)}
            </span>
          </div>
        </div>
      </div>
      
      {!isCurrent && onTerminate && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onTerminate(session.session_id)}
          disabled={isTerminating}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`terminate-session-${session.session_id}`}
        >
          {isTerminating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <LogOut className="w-4 h-4 mr-1" />
              Terminate
            </>
          )}
        </Button>
      )}
    </div>
  );
};

/**
 * Termination reason configuration for badges
 */
const TERMINATION_REASONS = {
  manual_logout: { label: 'Logged Out', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  user_self_terminate: { label: 'Terminated by You', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  user_terminate_all_others: { label: 'Terminated by You', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  admin_terminate: { label: 'Terminated by Admin', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  admin_action: { label: 'Terminated by Admin', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  expired: { label: 'Session Expired', color: 'bg-gray-100 text-gray-600 border-gray-300' },
  password_changed: { label: 'Password Changed', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  security_logout: { label: 'Security Logout', color: 'bg-red-100 text-red-700 border-red-300' },
  forced_logout: { label: 'Forced Logout', color: 'bg-red-100 text-red-700 border-red-300' },
};

/**
 * Calculate session duration
 */
const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 'Unknown';
  
  // Ensure UTC parsing
  const start = new Date(startDate.endsWith('Z') ? startDate : startDate + 'Z');
  const end = new Date(endDate.endsWith('Z') ? endDate : endDate + 'Z');
  
  const diffMs = end - start;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffDays > 0) {
    const remainingHours = Math.floor((diffMs % 86400000) / 3600000);
    return `${diffDays}d ${remainingHours}h`;
  }
  if (diffHours > 0) {
    const remainingMins = Math.floor((diffMs % 3600000) / 60000);
    return `${diffHours}h ${remainingMins}m`;
  }
  return `${diffMins}m`;
};

/**
 * Format date for history display (IST)
 */
const formatHistoryDate = (dateString) => {
  if (!dateString) return 'Unknown';
  
  // Ensure UTC parsing
  const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  const date = new Date(utcString);
  
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * History Session Card Component - for terminated/inactive sessions
 */
const HistorySessionCard = ({ session }) => {
  const { browser, os } = parseUserAgent(session.user_agent);
  const reason = TERMINATION_REASONS[session.terminated_reason] || TERMINATION_REASONS.manual_logout;
  const duration = calculateDuration(session.created_at, session.terminated_at);

  return (
    <div className="flex items-start justify-between p-3 rounded-lg border bg-white border-slate-200 hover:bg-slate-50 transition-colors">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-slate-100">
          <DeviceIcon userAgent={session.user_agent} />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800 text-sm">
              {browser} on {os}
            </span>
            <Badge className={`text-xs ${reason.color}`}>
              {reason.label}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {session.ip_address && (
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {session.ip_address}
              </span>
            )}
            {(session.geo_city || session.geo_country) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {[session.geo_city, session.geo_country].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatHistoryDate(session.created_at)}
            </span>
            <span className="text-slate-400">→</span>
            <span className="flex items-center gap-1">
              {formatHistoryDate(session.terminated_at)}
            </span>
          </div>
          <div className="text-xs text-slate-400">
            Duration: {duration}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PERMISSIONS TAB
// =============================================================================

const PermissionsTab = () => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/api/v1/users/me/permissions');
      setPermissions(response.data);
    } catch (error) {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'User Management': '👥',
      'Parts Catalogue': '📦',
      'Inventory': '📋',
      'Taxonomy': '🏷️',
      'System': '⚙️',
      'Reports': '📊'
    };
    return icons[category] || '📄';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'User Management': 'bg-purple-50 border-purple-200',
      'Parts Catalogue': 'bg-blue-50 border-blue-200',
      'Inventory': 'bg-emerald-50 border-emerald-200',
      'Taxonomy': 'bg-amber-50 border-amber-200',
      'System': 'bg-slate-50 border-slate-200',
      'Reports': 'bg-cyan-50 border-cyan-200'
    };
    return colors[category] || 'bg-slate-50 border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
      </div>
    );
  }

  if (!permissions) {
    return (
      <div className="text-center py-12 text-slate-500">
        Unable to load permissions
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Your Role & Permissions
          </CardTitle>
          <CardDescription>
            Based on your role, you have access to the following capabilities in the system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 p-4 bg-[#101957]/5 rounded-lg">
            <div className="w-12 h-12 bg-[#101957] rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Your Role</p>
              <p className="text-xl font-semibold text-[#101957] capitalize">
                {permissions.role?.replace('_', ' ')}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-sm text-slate-500">Total Permissions</p>
              <p className="text-2xl font-bold text-[#101957]">
                {permissions.permission_count}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permissions by Category */}
      <Card>
        <CardHeader>
          <CardTitle>Permissions by Category</CardTitle>
          <CardDescription>
            Detailed breakdown of what you can do in each area of the system.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {permissions.permissions_by_category && Object.keys(permissions.permissions_by_category).length > 0 ? (
            Object.entries(permissions.permissions_by_category).map(([category, perms]) => (
              <div 
                key={category} 
                className={`p-4 rounded-lg border ${getCategoryColor(category)}`}
                data-testid={`permission-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <span>{getCategoryIcon(category)}</span>
                  {category}
                  <Badge variant="secondary" className="ml-auto">
                    {perms.length} permission{perms.length !== 1 ? 's' : ''}
                  </Badge>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {perms.map((perm) => (
                    <div 
                      key={perm.code} 
                      className="flex items-start gap-2 p-2 bg-white/60 rounded"
                      data-testid={`permission-${perm.code}`}
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{perm.name}</p>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No permissions assigned to your role.</p>
              <p className="text-sm">Contact an administrator for assistance.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const UserSettingsPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/api/v1/users/me');
      setCurrentUser(response.data);
    } catch (error) {
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
    // Update the auth context if available
    if (updateUser) {
      updateUser(updatedUser);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
            data-testid="settings-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-[#101957]">Settings</h1>
          <p className="text-slate-600 mt-1">
            Manage your profile, security, and permissions
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger 
              value="profile" 
              className="flex items-center gap-2"
              data-testid="profile-tab"
            >
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="sessions" 
              className="flex items-center gap-2"
              data-testid="sessions-tab"
            >
              <Monitor className="w-4 h-4" />
              Sessions
            </TabsTrigger>
            <TabsTrigger 
              value="permissions" 
              className="flex items-center gap-2"
              data-testid="permissions-tab"
            >
              <Shield className="w-4 h-4" />
              Permissions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab user={currentUser} onUpdate={handleProfileUpdate} />
          </TabsContent>

          <TabsContent value="sessions">
            <SessionsTab />
          </TabsContent>

          <TabsContent value="permissions">
            <PermissionsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserSettingsPage;
